import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import InvoicePrintModal from '../components/InvoicePrintModal';

const PurchaseEntry = () => {
  const { selectedFirmId, firms, company } = useContext(AuthContext);

  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [purchases, setPurchases] = useState([]);

  // Form State
  const [purchaseId, setPurchaseId] = useState(null);
  const [purchaseNo, setPurchaseNo] = useState('1');
  const [purchaseDate, setPurchaseDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [vendorId, setVendorId] = useState('');

  // Line items [{ productId, Qty, Rate, total }]
  const [items, setItems] = useState([
    { productId: '', Qty: 0, Rate: 0, total: 0 }
  ]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Print & WhatsApp Modal State
  const [isPrintOpen, setIsPrintOpen] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [waSettings, setWaSettings] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadMasters();
    loadPurchases();
  }, [selectedFirmId]);

  const loadMasters = async () => {
    try {
      const [vRes, pRes, waRes] = await Promise.all([
        api.get('/masters/vendors'),
        api.get(`/masters/products?firmId=${selectedFirmId}`),
        api.get('/whatsapp/settings').catch(() => null)
      ]);
      setVendors(vRes.data);
      setProducts(pRes.data);
      if (waRes?.data?.settings) {
        setWaSettings(waRes.data.settings);
      }
    } catch (err) {
      console.error('Error loading purchase masters', err);
    }
  };

  const loadPurchases = async () => {
    try {
      const res = await api.get(`/purchases?firmId=${selectedFirmId}`);
      setPurchases(res.data);
      if (res.data.length > 0 && !purchaseId) {
        const maxNo = Math.max(...res.data.map(p => Number(p.purchaseNo) || 0));
        setPurchaseNo(String(maxNo + 1));
      }
    } catch (err) {
      console.error('Error loading purchases', err);
    }
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;

    if (field === 'productId') {
      const prod = products.find(p => String(p.Id) === String(value));
      if (prod) {
        updated[index].Rate = prod.PRate || 0;
      }
    }

    const qty = parseFloat(updated[index].Qty) || 0;
    const rate = parseFloat(updated[index].Rate) || 0;
    updated[index].total = qty * rate;

    setItems(updated);
  };

  const addItemRow = () => {
    setItems([...items, { productId: '', Qty: 0, Rate: 0, total: 0 }]);
  };

  const removeItemRow = (index) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const grandTotal = items.reduce((sum, i) => sum + (parseFloat(i.total) || 0), 0);

  const handleSavePurchase = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!vendorId) {
      setError('Please select a supplier / vendor.');
      return;
    }

    const validItems = items.filter(i => i.productId && i.total > 0);
    if (validItems.length === 0) {
      setError('Please add at least one valid item to the purchase bill.');
      return;
    }

    const payload = {
      Id: purchaseId,
      purchaseNo: parseInt(purchaseNo) || 1,
      purchaseDate,
      vendorId,
      GranadTotal: grandTotal,
      subtotal: grandTotal,
      FirmId: selectedFirmId,
      FID: 4,
      items: validItems
    };

    setLoading(true);
    try {
      const saveRes = await api.post('/purchases', payload);
      const savedId = saveRes.data.id || purchaseId;
      setSuccess('Purchase bill saved successfully!');
      resetForm();
      loadPurchases();

      // Check if auto popup print/whatsapp setting is enabled (defaults to true)
      const autoPopupEnabled = waSettings ? Boolean(waSettings.AutoSendPurchase === 1 || waSettings.AutoSendPurchase === true) : true;

      if (savedId && autoPopupEnabled) {
        setTimeout(() => {
          handleOpenPrintModal(savedId);
        }, 300);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save purchase bill');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPrintModal = async (id) => {
    try {
      setLoading(true);
      const res = await api.get(`/purchases/${id}`);
      const { purchase: pData, details } = res.data;
      const currentFirm = firms.find(f => String(f.Id) === String(pData.FirmId || selectedFirmId));

      setPrintData({
        purchaseNo: pData.purchaseNo || pData.Id,
        purchaseDate: pData.purchaseDate,
        vendorName: pData.vendorName,
        vendorAddress: pData.vendorAddress,
        vendorMobile: pData.vendorMobile,
        firmName: pData.firmName || currentFirm?.firmName || company?.Comapny_Name || 'Hari Ganga Fish Syndicate',
        firmAddress: pData.firmAddress || currentFirm?.Address || company?.Comapny_Address || '',
        firmContact: pData.firmContact || currentFirm?.ContactNo || company?.Company_Mobile || '',
        items: details,
        GranadTotal: pData.GranadTotal || pData.subtotal,
        balance: pData.balance || 0
      });
      setIsPrintOpen(true);
    } catch (err) {
      console.error('Error opening purchase print modal', err);
      setError('Failed to load purchase bill for printing');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewCurrentBill = () => {
    const currentVendor = vendors.find(v => String(v.Id) === String(vendorId));
    const currentFirm = firms.find(f => String(f.Id) === String(selectedFirmId));

    const populatedItems = items
      .filter(i => i.productId)
      .map(i => {
        const p = products.find(prod => String(prod.Id) === String(i.productId));
        return {
          ...i,
          productName: p ? p.productName : 'Item'
        };
      });

    if (populatedItems.length === 0) {
      alert('प्रिंट करण्यासाठी किमान एक आयटम जोडा (Please add at least one item to preview)');
      return;
    }

    setPrintData({
      purchaseNo: purchaseNo || '1',
      purchaseDate: purchaseDate,
      vendorName: currentVendor ? currentVendor.Name : 'Supplier',
      vendorAddress: currentVendor ? currentVendor.Address : '',
      vendorMobile: currentVendor ? currentVendor.MobileNo : '',
      firmName: currentFirm?.firmName || company?.Comapny_Name || 'Hari Ganga Fish Syndicate',
      firmAddress: currentFirm?.Address || company?.Comapny_Address || 'Sakhar Koliwada, Alibag, Raigad',
      firmContact: currentFirm?.ContactNo || company?.Company_Mobile || '9881914831',
      items: populatedItems,
      GranadTotal: grandTotal,
      balance: 0
    });
    setIsPrintOpen(true);
  };

  const handleEditPurchase = async (purchase) => {
    try {
      setLoading(true);
      const res = await api.get(`/purchases/${purchase.Id}`);
      const { purchase: pData, details } = res.data;

      setPurchaseId(pData.Id);
      setPurchaseNo(String(pData.purchaseNo));
      setPurchaseDate(pData.purchaseDate);
      setVendorId(pData.vendorId);

      setItems(details.length > 0 ? details : [{ productId: '', Qty: 0, Rate: 0, total: 0 }]);
    } catch (err) {
      setError('Failed to load purchase details');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePurchase = async (id) => {
    if (!window.confirm('Delete purchase record?')) return;
    try {
      await api.delete(`/purchases/${id}`);
      setSuccess('Purchase deleted');
      loadPurchases();
    } catch (err) {
      setError('Failed to delete purchase');
    }
  };

  const resetForm = () => {
    setPurchaseId(null);
    setVendorId('');
    setItems([{ productId: '', Qty: 0, Rate: 0, total: 0 }]);
  };

  return (
    <div className="app-container container-fluid py-3 px-2 px-md-4">
      <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
        <div>
          <h3 className="fw-extrabold mb-1 text-primary fs-4" style={{ fontFamily: 'var(--app-font-heading)' }}>
            <i className="bi bi-cart-check me-2"></i>खरेदी केलेला सामान / Purchase Entry
          </h3>
          <p className="text-muted mb-0 small">Record diesel, ice, kirana, and fish purchases from suppliers</p>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}
      {success && <div className="alert alert-success py-2 small">{success}</div>}

      <div className="row g-4">
        {/* FORM */}
        <div className="col-12 col-xl-8">
          <form onSubmit={handleSavePurchase}>
            {/* HEADER CARD */}
            <div className="card border-0 shadow-sm mb-4 rounded-3 overflow-hidden">
              <div className="card-header bg-primary text-white fw-bold d-flex justify-content-between align-items-center py-3 px-4">
                <span className="d-flex align-items-center gap-2 fs-6">
                  <i className="bi bi-file-earmark-spreadsheet-fill fs-5"></i>
                  खरेदी बील / Purchase Bill Header
                </span>
                {purchaseId && <span className="badge bg-warning text-dark px-3 py-1 fs-6">Editing Bill #{purchaseNo}</span>}
              </div>
              <div className="card-body p-4 bg-white">
                <div className="row g-3">
                  <div className="col-12 col-sm-6 col-md-3">
                    <label className="form-label fw-bold small text-dark d-flex align-items-center">
                      <i className="bi bi-hash text-primary me-1"></i>
                      बील क्रमांक (Bill No) <span className="text-danger ms-1">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-lg fw-bold border-primary-subtle shadow-sm"
                      value={purchaseNo}
                      onChange={(e) => setPurchaseNo(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-12 col-sm-6 col-md-3">
                    <label className="form-label fw-bold small text-dark d-flex align-items-center">
                      <i className="bi bi-calendar3 text-primary me-1"></i>
                      तारीख (Date) <span className="text-danger ms-1">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-lg border-primary-subtle shadow-sm"
                      value={purchaseDate}
                      onChange={(e) => setPurchaseDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-bold small text-dark d-flex align-items-center">
                      <i className="bi bi-person-badge-fill text-primary me-1"></i>
                      विक्रेता नांव (Supplier / Vendor) <span className="text-danger ms-1">*</span>
                    </label>
                    <select
                      className="form-select form-select-lg fw-semibold border-primary-subtle shadow-sm"
                      value={vendorId}
                      onChange={(e) => setVendorId(e.target.value)}
                      required
                    >
                      <option value="">-- सप्लायर / विक्रेता निवडा --</option>
                      {vendors.map(v => (
                        <option key={v.Id} value={v.Id}>
                          {v.Name} {v.Address ? `(${v.Address})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* ITEMS CARD */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                <h6 className="fw-bold mb-0 text-dark">
                  <i className="bi bi-box me-2 text-primary"></i>खरेदी सामान यादी / Purchase Items
                </h6>
                <button type="button" className="btn btn-outline-primary btn-sm rounded-pill px-3" onClick={addItemRow}>
                  <i className="bi bi-plus-lg me-1"></i> नवीन ओळ (+ Add Item)
                </button>
              </div>

              {/* MOBILE ITEM CARDS VIEW (< 768px) */}
              <div className="d-block d-md-none p-2 bg-light">
                {items.map((row, idx) => (
                  <div key={idx} className="card border mb-3 shadow-sm rounded-3">
                    <div className="card-header bg-light py-2 d-flex justify-content-between align-items-center">
                      <span className="fw-bold small text-primary">सामान # {idx + 1}</span>
                      {items.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm py-0 px-2"
                          onClick={() => removeItemRow(idx)}
                        >
                          <i className="bi bi-trash"></i> काढून टाका
                        </button>
                      )}
                    </div>
                    <div className="card-body p-3">
                      <div className="mb-3">
                        <label className="form-label fw-semibold small">सामान / खर्च प्रकार (Item Name)</label>
                        <select
                          className="form-select"
                          value={row.productId}
                          onChange={(e) => handleItemChange(idx, 'productId', e.target.value)}
                        >
                          <option value="">-- सामान निवडा --</option>
                          {products.map(p => (
                            <option key={p.Id} value={p.Id}>{p.productName} ({p.unit})</option>
                          ))}
                        </select>
                      </div>

                      <div className="row g-2 mb-3">
                        <div className="col-6">
                          <label className="form-label small text-muted mb-1">नग Qty</label>
                          <input
                            type="number"
                            className="form-control text-center fw-bold"
                            value={row.Qty}
                            onChange={(e) => handleItemChange(idx, 'Qty', e.target.value)}
                          />
                        </div>
                        <div className="col-6">
                          <label className="form-label small text-muted mb-1">दर Rate (₹)</label>
                          <input
                            type="number"
                            step="0.1"
                            className="form-control text-center fw-bold text-primary"
                            value={row.Rate}
                            onChange={(e) => handleItemChange(idx, 'Rate', e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                        <span className="small text-muted fw-bold">एकूण (Item Total):</span>
                        <span className="fw-bold fs-5 text-danger">₹{(row.total || 0).toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* DESKTOP TABLE VIEW (>= 768px) */}
              <div className="table-responsive d-none d-md-block" style={{ overflowX: 'auto' }}>
                <table className="table table-bordered align-middle mb-0 small" style={{ minWidth: '600px' }}>
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: '220px' }}>सामान नांव (Item Name)</th>
                      <th style={{ minWidth: '100px' }}>नग Qty</th>
                      <th style={{ minWidth: '110px' }}>दर Rate</th>
                      <th style={{ minWidth: '130px' }}>एकूण Total (₹)</th>
                      <th style={{ minWidth: '40px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row, idx) => (
                      <tr key={idx}>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={row.productId}
                            onChange={(e) => handleItemChange(idx, 'productId', e.target.value)}
                          >
                            <option value="">-- सामान निवडा --</option>
                            {products.map(p => (
                              <option key={p.Id} value={p.Id}>{p.productName} ({p.unit})</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm text-center"
                            value={row.Qty}
                            onChange={(e) => handleItemChange(idx, 'Qty', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.1"
                            className="form-control form-control-sm text-center fw-bold"
                            value={row.Rate}
                            onChange={(e) => handleItemChange(idx, 'Rate', e.target.value)}
                          />
                        </td>
                        <td className="fw-bold text-danger">₹{(row.total || 0).toLocaleString('en-IN')}</td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm p-0 px-2"
                            onClick={() => removeItemRow(idx)}
                          >
                            <i className="bi bi-x"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* CARD FOOTER */}
              <div className="card-footer bg-light d-flex justify-content-between align-items-center py-3">
                <span className="fw-bold fs-6">एकूण खरेदी बील (Grand Total):</span>
                <span className="fs-3 fw-extrabold text-danger">₹{grandTotal.toLocaleString('en-IN')}</span>
              </div>
            </div>

            <div className="d-flex gap-2 mb-4 flex-wrap">
              <button type="submit" className="btn btn-primary btn-lg flex-grow-1 fw-bold shadow-sm" disabled={loading}>
                <i className="bi bi-check-circle me-2"></i> बील सेव्ह करा (Save Purchase)
              </button>
              <button
                type="button"
                className="btn btn-outline-dark btn-lg fw-bold shadow-sm"
                onClick={handlePreviewCurrentBill}
                disabled={items.filter(i => i.productId).length === 0}
                title="खरेदी बील प्रिंट किंवा व्हॉट्सॲपवर पाहण्यासाठी"
              >
                <i className="bi bi-printer me-2"></i> प्रिंट / व्हॉट्सॲप (Print / Share)
              </button>
              {purchaseId && (
                <button type="button" className="btn btn-outline-secondary btn-lg" onClick={resetForm}>
                  रद्द करा (Cancel)
                </button>
              )}
            </div>
          </form>
        </div>

        {/* RECENT PURCHASES HISTORY TABLE */}
        <div className="col-12 col-xl-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
              <h6 className="fw-bold mb-0 text-dark">
                <i className="bi bi-clock-history me-2 text-primary"></i>मागील खरेदी (Purchase History)
              </h6>
              <span className="badge bg-primary-subtle text-primary fw-semibold">{purchases.length} Bills</span>
            </div>
            <div className="table-responsive" style={{ maxHeight: '550px' }}>
              <table className="table table-hover align-middle mb-0 small">
                <thead className="table-light sticky-top">
                  <tr>
                    <th>बील #</th>
                    <th>सप्लायर (Supplier)</th>
                    <th>एकूण (Total)</th>
                    <th className="text-end">कृती (Actions)</th>
                  </tr>
                </thead>
                <tbody>
                  {purchases.map(p => (
                    <tr key={p.Id}>
                      <td className="fw-bold">#{p.purchaseNo || p.Id}</td>
                      <td>
                        <div className="fw-semibold">{p.vendorName || 'Supplier'}</div>
                        {p.vendorMobile && (
                          <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                            <i className="bi bi-phone me-1"></i>{p.vendorMobile}
                          </div>
                        )}
                      </td>
                      <td className="text-danger fw-bold">₹{(p.GranadTotal || 0).toLocaleString('en-IN')}</td>
                      <td className="text-end">
                        <div className="btn-group btn-group-sm">
                          <button
                            type="button"
                            className="btn btn-outline-dark py-0 px-2"
                            title="खरेदी बील प्रिंट करा (Print Bill)"
                            onClick={() => handleOpenPrintModal(p.Id)}
                          >
                            <i className="bi bi-printer"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-success py-0 px-2"
                            title="सप्लायरला व्हॉट्सॲप पाठवा (Send WhatsApp)"
                            onClick={() => handleOpenPrintModal(p.Id)}
                          >
                            <i className="bi bi-whatsapp"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-primary py-0 px-2"
                            title="बदला (Edit)"
                            onClick={() => handleEditPurchase(p)}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger py-0 px-2"
                            title="हटवा (Delete)"
                            onClick={() => handleDeletePurchase(p.Id)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {purchases.length === 0 && (
                    <tr>
                      <td colSpan="4" className="text-center text-muted py-3">
                        कोणतीही खरेदी नोंद उपलब्ध नाही (No purchases found)
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* PURCHASE BILL ONLINE PRINT & WHATSAPP MODAL */}
      <InvoicePrintModal
        isOpen={isPrintOpen}
        onClose={() => setIsPrintOpen(false)}
        title="खरेदी बील / Supplier Purchase Bill"
        invoiceType="purchase"
        data={printData}
      />
    </div>
  );
};

export default PurchaseEntry;
